import { HttpClient, HttpContext } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BYPASS_BUSINESS_SCOPING } from '../interceptors/business-scoping-map';
import { BusinessContextService } from '../state/business';
import { BusinessQuery } from '../state/business/business.query';
import { DefaultResponse, SessionQuery } from '../state/session';

/**
 * Deep-link auto-switch guard.
 *
 * When a resource route (e.g. /project/:id) is opened while a different
 * business is active, this resolves the resource's business, switches to it
 * (silently — the requested URL wins, so it uses the navigate-free primitive,
 * NOT switchBusiness), and lets activation proceed. Super-admins bypass the
 * membership check (mirroring the API). A non-member, non-admin gets the shared
 * 403 experience; a missing resource falls through to the existing 404 path.
 *
 * The metadata fetch carries BYPASS_BUSINESS_SCOPING so the still-active old
 * business does not scope it — otherwise a business-B resource would 404 while
 * A is active.
 */
export const businessContextGuard: CanActivateFn = (route: ActivatedRouteSnapshot): Observable<boolean> => {
	const http = inject(HttpClient);
	const businessContext = inject(BusinessContextService);
	const businessQuery = inject(BusinessQuery);
	const sessionQuery = inject(SessionQuery);
	const router = inject(Router);
	const snackBar = inject(MatSnackBar);

	const id = route.paramMap.get('id');
	if (!id) {
		return of(true);
	}

	const denyAccess = (): boolean => {
		snackBar.open($localize`:@@deepLinkNoAccess:You don't have access to this resource.`, undefined, { duration: 5000 });
		router.navigateByUrl('/');
		return false;
	};

	const context = new HttpContext().set(BYPASS_BUSINESS_SCOPING, true);
	return http.get<DefaultResponse<{ businessId?: string }>>(`${environment.apiUrl}/projects/${id}`, { context }).pipe(
		map(resp => {
			const businessId = resp?.data?.businessId;
			// Can't determine the resource's business — let the page load and
			// handle it (keeps the guard non-blocking on unexpected shapes).
			if (!businessId || businessId === businessContext.getActiveBusinessId()) {
				return true;
			}
			const isMember = !!businessQuery.getUserBusiness(businessId);
			if (!isMember && !sessionQuery.isAtLeastSuperAdmin()) {
				return denyAccess();
			}
			businessContext.setActiveBusinessContext(businessId);
			return true;
		}),
		catchError(err => {
			// Missing resource → let the existing 404 handling take over.
			if (err?.status === 404) {
				return of(true);
			}
			return of(denyAccess());
		})
	);
};
